import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiExternalLink, FiGithub, FiCalendar, FiCpu, FiShield, FiZap, FiTrendingUp, FiCheck, FiClock, FiAlertCircle } from 'react-icons/fi';

type ReportTab = 'february-technical' | 'february-alpha';

const GITHUB_REPO = 'https://github.com/qfintech/cab-bot-v3';

const CabBotReportsPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>('february-technical');

  const tabs = [
    { 
      id: 'february-technical' as ReportTab, 
      label: 'Technical Progress Report', 
      subtitle: 'V2.10 Milestones',
      date: 'February 11, 2026',
      badge: 'January Work'
    },
    { 
      id: 'february-alpha' as ReportTab, 
      label: 'Technical Alpha Thesis', 
      subtitle: 'V2 → V3 Evolution',
      date: 'February 11, 2026',
      badge: 'Strategic'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-xl border border-orange-500/30">
              <FiCpu className="text-orange-400 text-lg" />
            </div>
            CAB Bot V2 — Reports
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            MODV-CAB V2.0 Joint Venture &middot; SnapChart AI
          </p>
        </div>
        <a
          href={GITHUB_REPO}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:text-white hover:border-slate-600 transition-all text-sm"
        >
          <FiGithub className="text-base" />
          View Repository
          <FiExternalLink className="text-xs opacity-60" />
        </a>
      </div>

      {/* Report Tabs */}
      <div className="flex gap-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex-1 p-4 rounded-xl border transition-all text-left ${
              activeTab === tab.id
                ? 'bg-slate-800/80 border-orange-500/40 shadow-lg shadow-orange-500/5'
                : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-semibold ${activeTab === tab.id ? 'text-orange-300' : 'text-slate-300'}`}>
                {tab.label}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">
                {tab.badge}
              </span>
            </div>
            <p className="text-xs text-slate-500">{tab.subtitle}</p>
            <div className="flex items-center gap-1 mt-2 text-[11px] text-slate-500">
              <FiCalendar className="text-[10px]" />
              {tab.date}
            </div>
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTabIndicator"
                className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
              />
            )}
          </button>
        ))}
      </div>

      {/* Report Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'february-technical' ? <TechnicalProgressReport /> : <AlphaThesisReport />}
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          <span className="text-slate-400 font-medium">CLASSIFICATION:</span> PROPRIETARY & CONFIDENTIAL
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span>TO: Magnum Opus Management LLC</span>
          <span>FROM: William Coyle (CEO) & Jean-Michel Portal-Gosselin (CTO)</span>
        </div>
      </div>
    </div>
  );
};

/* ========================================
   REPORT 1: Technical Progress Report
   ======================================== */
const TechnicalProgressReport: React.FC = () => (
  <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
    {/* Report Header */}
    <div className="bg-gradient-to-r from-orange-600/10 via-slate-800/50 to-amber-600/10 px-6 py-5 border-b border-slate-700/40">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Technical Progress Report</h3>
          <p className="text-sm text-slate-400 mt-0.5">
            Project: MODV-CAB V2.0 Joint Venture (V3) • System: V3Bot – Version 2.10 (Solana HFT Engine)
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Delivered</div>
          <div className="text-sm text-orange-300 font-semibold">February 11, 2026</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Work period: January 2026</div>
        </div>
      </div>
    </div>

    <div className="p-6 space-y-6">
      {/* Executive Overview */}
      <ReportSection icon={FiFileText} title="I. Executive Overview">
        <p className="text-slate-300 text-sm leading-relaxed">
          Since the effective date of January 10, 2026, the development of V3Bot 2.10 has transitioned into its <span className="text-orange-300 font-medium">final integration phase</span>. Under the technical direction of Jean-Michel Portal-Gosselin, we have successfully pivoted from a dependency-heavy model to a <span className="text-orange-300 font-medium">sovereign, local-engine architecture</span>.
        </p>
      </ReportSection>

      {/* Core Infrastructure */}
      <ReportSection icon={FiCpu} title="II. Core Infrastructure: The Local Engine">
        <p className="text-slate-300 text-sm mb-4 leading-relaxed">
          The primary technical breakthrough in V2.10 is the <span className="text-white font-medium">Solana Local Pricing & State Engine</span>. Unlike standard bots that rely on external API calls, V3Bot now maintains a live, in-memory replica of the Solana ledger state for targeted liquidity pools.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="Latency" value="< 1.0ms" sub="Internal price discovery" />
          <MetricCard label="API RTT" value="Zero" sub="No external request-response cycles" />
          <MetricCard label="Mempool" value="Real-time" sub="gRPC stream sync" />
        </div>
      </ReportSection>

      {/* Milestones */}
      <ReportSection icon={FiTrendingUp} title="III. Milestones Achieved (V2.10)">
        <div className="space-y-4">
          <MilestoneItem 
            number={1}
            title="High-Performance Core Optimization"
            metric="150,000+ account updates/sec"
            detail="Minimal CPU overhead, zero memory leaks over 72-hour test cycles"
          />
          <MilestoneItem 
            number={2}
            title="Advanced Capital Protection (Atomic Execution)"
            metric="Jito Bundles Integration"
            detail="All-or-nothing execution — zero cost, zero loss if profit condition not met"
          />
          <MilestoneItem 
            number={3}
            title="Proprietary Liquidity Filtering"
            metric="Automated Health Check"
            detail="Filters rug-pull tokens and low-liquidity traps automatically"
          />
        </div>
      </ReportSection>

      {/* IP & Security */}
      <ReportSection icon={FiShield} title="IV. Intellectual Property & Security">
        <div className="grid grid-cols-3 gap-3">
          <IPCard title="Logic Abstraction" desc="Proprietary formulas isolated in pre-compiled binary modules" />
          <IPCard title="Non-Persistent Keys" desc="Secure environment injection, never stored in plain text" />
          <IPCard title="Obfuscated Strategy" desc="Trading patterns randomized to prevent copy-trading" />
        </div>
      </ReportSection>

      {/* Roadmap */}
      <ReportSection icon={FiCalendar} title="V. Roadmap to Full Deployment">
        <div className="space-y-2">
          <RoadmapPhase phase={1} title="Local Engine Core Development" status="completed" />
          <RoadmapPhase phase={2} title="Jito/gRPC Integration & Stress Test" status="completed" />
          <RoadmapPhase phase={3} title="Adaptive Slippage & UX Dashboard" status="in-progress" />
          <RoadmapPhase phase={4} title="Full Institutional Scale-up" status="pending" />
        </div>
      </ReportSection>

      {/* Final Statement */}
      <div className="bg-slate-700/20 border border-slate-600/30 rounded-xl p-4">
        <p className="text-slate-300 text-sm italic leading-relaxed">
          "The V3Bot 2.10 is no longer a prototype; it is a specialized HFT engine designed for the specific volatility and speed of the Solana ecosystem. We are moving toward the final production audit with full confidence in the system's stability and speed."
        </p>
      </div>
    </div>
  </div>
);

/* ========================================
   REPORT 2: Alpha Thesis Report
   ======================================== */
const AlphaThesisReport: React.FC = () => (
  <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
    {/* Report Header */}
    <div className="bg-gradient-to-r from-amber-600/10 via-slate-800/50 to-orange-600/10 px-6 py-5 border-b border-slate-700/40">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">V3Bot: The Technical Alpha Thesis</h3>
          <p className="text-sm text-slate-400 mt-0.5">
            Architectural Evolution: From Prototype (V2) to Institutional Engine (V3)
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Subject</div>
          <div className="text-sm text-amber-300 font-semibold">Sovereign Infrastructure</div>
          <div className="text-[10px] text-slate-500 mt-0.5">& Market Dominance</div>
        </div>
      </div>
    </div>

    <div className="p-6 space-y-6">
      {/* Paradigm Shift */}
      <ReportSection icon={FiZap} title="I. The Paradigm Shift">
        <p className="text-slate-300 text-sm mb-4 leading-relaxed">
          In the world of Solana HFT, latency isn't just a metric; it is the boundary between profit and noise. Version 3 represents a <span className="text-amber-300 font-medium">total decoupling from external dependencies</span> — from API-dependency to Infrastructure Sovereignty.
        </p>
        {/* Comparison Table */}
        <div className="bg-slate-900/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="grid grid-cols-3 gap-px bg-slate-700/30">
            <div className="bg-slate-800 p-3 text-xs font-semibold text-slate-400">Metric</div>
            <div className="bg-slate-800 p-3 text-xs font-semibold text-red-400/70">V2 Baseline</div>
            <div className="bg-slate-800 p-3 text-xs font-semibold text-emerald-400">V3 Achievement</div>
          </div>
          <ComparisonRow metric="End-to-End Latency" v2="150-300ms" v3="15-50ms (10X)" />
          <ComparisonRow metric="Price Discovery" v2="External APIs" v3="Local Engine (<1μs)" />
          <ComparisonRow metric="Pool Coverage" v2="143 pools" v3="148,000+ pools" />
          <ComparisonRow metric="Success Rate" v2="40% Capture" v3="85-90% Capture" />
        </div>
      </ReportSection>

      {/* Local Engine */}
      <ReportSection icon={FiCpu} title="II. The Local Engine: Our Strategic Moat">
        <div className="space-y-4">
          <FeatureBlock 
            number={1}
            title="Zero-Latency Execution"
            text="We no longer 'ask' the market for a price. We calculate it internally in sub-microsecond intervals using lock-free data structures."
          />
          <FeatureBlock 
            number={2}
            title="Deep gRPC Integration"
            text="Yellowstone gRPC streams synchronize 148,000+ liquidity pools in real-time. 10,000 pool updates per second — a 20X increase over V2."
          />
          <FeatureBlock 
            number={3}
            title="Institutional Hardening"
            text="Modular Microservice Architecture: 19 → 34 specialized modules. 99.95% uptime with self-healing capabilities."
          />
        </div>
      </ReportSection>

      {/* Technical Alpha */}
      <ReportSection icon={FiTrendingUp} title="III. Technical Alpha & Profitability">
        <p className="text-slate-300 text-sm mb-4 leading-relaxed">
          Technical Alpha is the profit generated purely through <span className="text-amber-300 font-medium">superior engineering</span>. V3 converts engineering excellence into a measurable P&L advantage.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="Capture Rate" value="2X" sub="From 395ms → 15ms hot-path" />
          <MetricCard label="Security" value="Atomic" sub="Jito multi-region fan-out" />
          <MetricCard label="Scale" value="$500K-$5M+" sub="Optimal deployment range" />
        </div>
      </ReportSection>

      {/* Path to Dominance */}
      <ReportSection icon={FiShield} title="IV. The Path to Dominance">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-700/20 border border-slate-600/30 rounded-xl p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Current State</div>
            <div className="text-sm text-white font-medium">Production-ready</div>
            <div className="text-xs text-slate-400 mt-1">10X faster, autonomous</div>
          </div>
          <div className="bg-slate-700/20 border border-slate-600/30 rounded-xl p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Target</div>
            <div className="text-sm text-white font-medium">Tier-1 Professional</div>
            <div className="text-xs text-slate-400 mt-1">Competing with institutional MMs</div>
          </div>
          <div className="bg-slate-700/20 border border-slate-600/30 rounded-xl p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Tech Asset Value</div>
            <div className="text-sm text-emerald-400 font-bold">$2M+</div>
            <div className="text-xs text-slate-400 mt-1">From $100K prototype</div>
          </div>
        </div>
      </ReportSection>

      {/* Closing */}
      <div className="bg-slate-700/20 border border-slate-600/30 rounded-xl p-4">
        <p className="text-slate-300 text-sm italic leading-relaxed">
          "The transition to V3 marks the end of our 'Build' phase and the beginning of our 'Dominance' phase. We have built a moat that is not easily crossed: a system that is faster, smarter, and entirely under our control."
        </p>
      </div>
    </div>
  </div>
);

/* ========================================
   SHARED SUB-COMPONENTS
   ======================================== */

const ReportSection: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode }> = ({ icon: Icon, title, children }) => (
  <div>
    <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
      <Icon className="text-orange-400 text-sm" />
      {title}
    </h4>
    {children}
  </div>
);

const MetricCard: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
  <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3 text-center">
    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
    <div className="text-lg font-bold text-orange-300 mt-1">{value}</div>
    <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
  </div>
);

const MilestoneItem: React.FC<{ number: number; title: string; metric: string; detail: string }> = ({ number, title, metric, detail }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-400">
      {number}
    </div>
    <div>
      <div className="text-sm font-medium text-white">{title}</div>
      <div className="text-xs text-orange-300/80 mt-0.5">{metric}</div>
      <div className="text-xs text-slate-500 mt-0.5">{detail}</div>
    </div>
  </div>
);

const IPCard: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <div className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-3">
    <div className="text-xs font-semibold text-white mb-1">{title}</div>
    <div className="text-[11px] text-slate-500 leading-relaxed">{desc}</div>
  </div>
);

const RoadmapPhase: React.FC<{ phase: number; title: string; status: 'completed' | 'in-progress' | 'pending' }> = ({ phase, title, status }) => {
  const statusConfig = {
    completed: { icon: FiCheck, label: 'Completed', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
    'in-progress': { icon: FiClock, label: 'In Progress', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
    pending: { icon: FiAlertCircle, label: 'Pending', color: 'text-slate-400 bg-slate-500/10 border-slate-500/20' },
  };
  const cfg = statusConfig[status];
  const Icon = cfg.icon;

  return (
    <div className="flex items-center justify-between bg-slate-900/30 border border-slate-700/30 rounded-lg px-4 py-2.5">
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-slate-500">Phase {phase}</span>
        <span className="text-sm text-slate-300">{title}</span>
      </div>
      <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${cfg.color}`}>
        <Icon className="text-[10px]" />
        {cfg.label}
      </div>
    </div>
  );
};

const ComparisonRow: React.FC<{ metric: string; v2: string; v3: string }> = ({ metric, v2, v3 }) => (
  <div className="grid grid-cols-3 gap-px bg-slate-700/20">
    <div className="bg-slate-800/80 p-3 text-xs text-slate-300">{metric}</div>
    <div className="bg-slate-800/80 p-3 text-xs text-red-400/60">{v2}</div>
    <div className="bg-slate-800/80 p-3 text-xs text-emerald-400 font-medium">{v3}</div>
  </div>
);

const FeatureBlock: React.FC<{ number: number; title: string; text: string }> = ({ number, title, text }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-400">
      {number}
    </div>
    <div>
      <div className="text-sm font-medium text-white">{title}</div>
      <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{text}</div>
    </div>
  </div>
);

export default CabBotReportsPanel;
